/**
 * End the shared session (ORDR-204).
 *
 * The framework's account controls (`PaioAuthButton`, `UserAccountMenu`,
 * `UserProfileFooter` in `@pirouz-ca/ui-components`) end no session themselves:
 * each draws "Sign out" and calls the host's `onSignOut`. `@pirouz-ca/ui-auth`
 * ships no sign-out helper either. So the call is this app's, through its one
 * HTTP seam and therefore the gateway, as measured on paio-auth origin/main
 * (`api/v1/session_views.py`, `SessionRevokeView`):
 *
 * - `POST /auth/session/revoke`, no body. It deletes the `paio_sid` session,
 *   answers with a `Max-Age=0` cookie, and the next `/auth/session` is
 *   anonymous. The cookie is the gateway host's, so this ends the session for
 *   every app in the browser that shares it, which is what "sign out" means
 *   to the person who asked.
 * - No CSRF token: the view has no authentication classes, and `paio_sid` is
 *   `SameSite=Lax`, so a cross-site page cannot force it.
 *
 * Only a 2xx is a sign-out. Anything else means the person is still signed
 * in, and the caller says so rather than landing them on a sign-in screen
 * that would bounce them straight back.
 */

import type { ApiClient } from "../api/client.js";

export type SignOutResult =
  | { readonly kind: "signed-out" }
  | { readonly kind: "failed"; readonly correlationId: string };

export async function signOut(client: ApiClient): Promise<SignOutResult> {
  const response = await client.request("/auth/session/revoke", { method: "POST" });
  return response.status !== null && response.status >= 200 && response.status < 300
    ? { kind: "signed-out" }
    : { kind: "failed", correlationId: response.correlationId };
}
